import { useState, useEffect } from '@wordpress/element';
import { __, sprintf } from '@wordpress/i18n';

const createDefaultCondition = () => ({
  id: Date.now() + Math.floor(Math.random() * 1000),
  node_type: 'condition',
  type: 'everywhere',
  logic: 'show',
  specific_items: [],
  categories: [],
  taxonomies: [],
  post_type_value: 'post',
  taxonomy_value: 'category',
});

const createDefaultGroup = () => ({
  id: Date.now() + Math.floor(Math.random() * 1000),
  node_type: 'group',
  logic: 'show',
  relation: 'any',
  children: [createDefaultCondition()],
});

const normalizeConditionNode = (node) => {
  if (!node || typeof node !== 'object') {
    return null;
  }

  if (node.node_type === 'group') {
    const children = Array.isArray(node.children)
      ? node.children.map(normalizeConditionNode).filter(Boolean)
      : [];

    if (!children.length) {
      return null;
    }

    return {
      id: node.id || Date.now() + Math.floor(Math.random() * 1000),
      node_type: 'group',
      logic: node.logic === 'hide' ? 'hide' : 'show',
      relation: node.relation === 'all' ? 'all' : 'any',
      children,
    };
  }

  if (!node.type) {
    return null;
  }

  return {
    ...createDefaultCondition(),
    ...node,
    id: node.id || Date.now() + Math.floor(Math.random() * 1000),
    node_type: 'condition',
    logic: node.logic === 'hide' ? 'hide' : 'show',
  };
};

const normalizePayload = (payload) => {
  const normalized = {
    relation: 'any',
    exclude_relation: 'any',
    conditions: [],
  };

  if (!payload || typeof payload !== 'object') {
    return normalized;
  }

  if (Array.isArray(payload)) {
    normalized.conditions = payload.map(normalizeConditionNode).filter(Boolean);
    return normalized;
  }

  if (Array.isArray(payload.conditions)) {
    normalized.relation = payload.relation === 'all' ? 'all' : 'any';
    normalized.exclude_relation = payload.exclude_relation === 'all' ? 'all' : 'any';
    normalized.conditions = payload.conditions.map(normalizeConditionNode).filter(Boolean);
  }

  return normalized;
};

const HeaderFooterConditions = ({
  templateId,
  initialConditions = null,
  initialStatus = 'publish',
  initialPriority = 10,
  onSave = null,
  isEmbedded = false,
  onChange = null,
}) => {
  const initialData = normalizePayload(initialConditions);

  const [conditions, setConditions] = useState(
    initialData.conditions.length > 0 ? initialData.conditions : [createDefaultCondition()]
  );
  const [conditionRelation, setConditionRelation] = useState(initialData.relation);
  const [excludeRelation, setExcludeRelation] = useState(initialData.exclude_relation);
  const [priority, setPriority] = useState(initialPriority || 10);
  const [isActive, setIsActive] = useState(initialStatus === 'publish');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [conflicts, setConflicts] = useState([]);

  // Option caches
  const [cachedPosts, setCachedPosts] = useState({});
  const [cachedTerms, setCachedTerms] = useState({});

  const availablePostTypes = window.thhaAdminSettings?.postTypes || [
    { value: 'post', label: 'Post' },
    { value: 'page', label: 'Page' },
  ];
  const availableTaxonomies = window.thhaAdminSettings?.taxonomies || [
    { value: 'category', label: 'Category' },
    { value: 'post_tag', label: 'Tag' },
  ];

  const conditionTypes = [
    { value: 'everywhere', label: __('Everywhere', 'huge-addons-for-elementor') },
    { value: 'front_page', label: __('Front Page', 'huge-addons-for-elementor') },
    { value: 'blog_home', label: __('Blog / Posts Page', 'huge-addons-for-elementor') },
    { value: 'singular', label: __('All Singular Pages', 'huge-addons-for-elementor') },
    { value: 'post_type', label: __('Specific Post Type', 'huge-addons-for-elementor') },
    { value: 'post_type_singular', label: __('Post Type Single', 'huge-addons-for-elementor') },
    { value: 'specific_post', label: __('Specific Post', 'huge-addons-for-elementor') },
    { value: 'specific_page', label: __('Specific Page', 'huge-addons-for-elementor') },
    { value: 'archive', label: __('All Archives', 'huge-addons-for-elementor') },
    { value: 'category_archive', label: __('Category Archive', 'huge-addons-for-elementor') },
    { value: 'specific_category', label: __('Specific Category', 'huge-addons-for-elementor') },
    { value: 'tag_archive', label: __('Tag Archive', 'huge-addons-for-elementor') },
    { value: 'taxonomy_archive', label: __('Taxonomy Archive', 'huge-addons-for-elementor') },
    { value: 'search', label: __('Search Results', 'huge-addons-for-elementor') },
    { value: '404', label: __('404 Error Page', 'huge-addons-for-elementor') },
  ];

  const buildPayload = () => ({
    version: 2,
    relation: conditionRelation,
    exclude_relation: excludeRelation,
    conditions,
  });

  useEffect(() => {
    if (onChange) {
      onChange(buildPayload(), priority);
    }
  }, [conditions, conditionRelation, excludeRelation, priority]);

  // Fetch dynamic items for a post type
  const fetchPostsForType = async (postType) => {
    if (cachedPosts[postType]) return;

    try {
      const response = await fetch(
        `${window.thhaAdminSettings.ajaxurl}?action=thha_hf_get_posts_by_type&nonce=${window.thhaAdminSettings.nonce}&post_type=${postType}`
      );
      const data = await response.json();
      if (data.success) {
        setCachedPosts((prev) => ({ ...prev, [postType]: data.data || [] }));
      }
    } catch (e) {
      console.error('Error fetching posts:', e);
    }
  };

  // Fetch dynamic items for a taxonomy
  const fetchTermsForTaxonomy = async (taxonomy) => {
    if (cachedTerms[taxonomy]) return;

    try {
      const response = await fetch(
        `${window.thhaAdminSettings.ajaxurl}?action=thha_hf_get_terms_by_taxonomy&nonce=${window.thhaAdminSettings.nonce}&taxonomy=${taxonomy}`
      );
      const data = await response.json();
      if (data.success) {
        setCachedTerms((prev) => ({ ...prev, [taxonomy]: data.data || [] }));
      }
    } catch (e) {
      console.error('Error fetching terms:', e);
    }
  };

  const updateNodeById = (nodes, nodeId, updater) =>
    nodes.map((node) => {
      if (node.id === nodeId) {
        return updater(node);
      }
      if (node.node_type === 'group' && Array.isArray(node.children)) {
        return { ...node, children: updateNodeById(node.children, nodeId, updater) };
      }
      return node;
    });

  const removeNodeById = (nodes, nodeId) =>
    nodes
      .filter((node) => node.id !== nodeId)
      .map((node) => {
        if (node.node_type === 'group' && Array.isArray(node.children)) {
          return { ...node, children: removeNodeById(node.children, nodeId) };
        }
        return node;
      })
      .filter(
        (node) => !(node.node_type === 'group' && (!Array.isArray(node.children) || node.children.length === 0))
      );

  const handleAddCondition = () => {
    setConditions((prev) => [...prev, createDefaultCondition()]);
  };

  const handleAddGroup = () => {
    setConditions((prev) => [...prev, createDefaultGroup()]);
  };

  const handleRemoveCondition = (conditionId) => {
    setConditions((prev) => {
      const filtered = removeNodeById(prev, conditionId);
      if (filtered.length === 0) {
        return [createDefaultCondition()];
      }
      return filtered;
    });
  };

  const handleUpdateCondition = (conditionId, updates) => {
    setConditions((prev) => updateNodeById(prev, conditionId, (node) => ({ ...node, ...updates })));
  };

  const handleSave = async () => {
    if (isEmbedded) {
      if (onSave) onSave(buildPayload(), priority);
      return;
    }

    setLoading(true);
    setError('');
    setConflicts([]);

    try {
      const payload = buildPayload();
      const statusToSave = isActive ? 'publish' : 'draft';

      const formData = new URLSearchParams();
      formData.append('action', 'thha_hf_save_conditions');
      formData.append('nonce', window.thhaAdminSettings.saveNonce || window.thhaAdminSettings.nonce);
      formData.append('template_id', templateId);
      formData.append('conditions', JSON.stringify(payload));
      formData.append('status', statusToSave);
      formData.append('priority', priority);

      const response = await fetch(window.thhaAdminSettings.ajaxurl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      const res = await response.json();
      if (res.success) {
        if (res.data?.has_conflict) {
          setConflicts(res.data.conflicts || []);
          setIsActive(false);
          if (window.iziToast) {
            window.iziToast.warning({
              title: __('Conflict Warning', 'huge-addons-for-elementor'),
              message: res.data.message,
              position: 'topRight',
            });
          }
        } else {
          if (window.iziToast) {
            window.iziToast.success({
              title: __('Saved', 'huge-addons-for-elementor'),
              message: res.data.message || __('Conditions saved successfully.', 'huge-addons-for-elementor'),
              position: 'topRight',
            });
          }
          if (onSave) onSave(payload, priority);
        }
      } else {
        setError(res.data?.message || __('Failed to save conditions.', 'huge-addons-for-elementor'));
      }
    } catch (err) {
      setError(__('An unexpected network error occurred.', 'huge-addons-for-elementor'));
    } finally {
      setLoading(false);
    }
  };

  const renderConditionNode = (node, depth = 0) => {
    if (node.node_type === 'group') {
      return (
        <div key={node.id} className="thha-condition-group" style={{ marginLeft: depth * 16 }}>
          <div className="thha-group-header">
            <span className="thha-group-label">
              {__('Condition Group', 'huge-addons-for-elementor')}
            </span>
            <div className="thha-group-controls">
              <select
                value={node.logic}
                onChange={(e) => handleUpdateCondition(node.id, { logic: e.target.value })}
                className="thha-select thha-select-logic"
              >
                <option value="show">{__('Include', 'huge-addons-for-elementor')}</option>
                <option value="hide">{__('Exclude', 'huge-addons-for-elementor')}</option>
              </select>
              <select
                value={node.relation}
                onChange={(e) => handleUpdateCondition(node.id, { relation: e.target.value })}
                className="thha-select"
              >
                <option value="any">{__('Match Any Rule (OR)', 'huge-addons-for-elementor')}</option>
                <option value="all">{__('Match All Rules (AND)', 'huge-addons-for-elementor')}</option>
              </select>
              <button
                type="button"
                className="thha-btn-delete"
                onClick={() => handleRemoveCondition(node.id)}
                title={__('Remove Group', 'huge-addons-for-elementor')}
              >
                ✕
              </button>
            </div>
          </div>
          <div className="thha-group-children">
            {node.children && node.children.map((child) => renderConditionNode(child, depth + 1))}
          </div>
          <div className="thha-group-actions">
            <button
              type="button"
              className="thha-btn-secondary thha-btn-sm"
              onClick={() =>
                handleUpdateCondition(node.id, {
                  children: [...(node.children || []), createDefaultCondition()],
                })
              }
            >
              + {__('Add Rule to Group', 'huge-addons-for-elementor')}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div key={node.id} className="thha-condition-row" style={{ marginLeft: depth * 16 }}>
        {/* Logic: Include / Exclude */}
        <select
          value={node.logic}
          onChange={(e) => handleUpdateCondition(node.id, { logic: e.target.value })}
          className={`thha-select thha-select-logic ${node.logic === 'hide' ? 'is-exclude' : 'is-include'}`}
        >
          <option value="show">{__('Include', 'huge-addons-for-elementor')}</option>
          <option value="hide">{__('Exclude', 'huge-addons-for-elementor')}</option>
        </select>

        {/* Condition Type */}
        <select
          value={node.type}
          onChange={(e) => {
            const newType = e.target.value;
            handleUpdateCondition(node.id, { type: newType });
            if (newType === 'specific_post') fetchPostsForType('post');
            if (newType === 'specific_page') fetchPostsForType('page');
            if (newType === 'specific_category') fetchTermsForTaxonomy('category');
          }}
          className="thha-select thha-select-type"
        >
          {conditionTypes.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>

        {/* Dynamic Post Type selector */}
        {(node.type === 'post_type' || node.type === 'post_type_singular') && (
          <select
            value={node.post_type_value || 'post'}
            onChange={(e) => {
              const pt = e.target.value;
              handleUpdateCondition(node.id, { post_type_value: pt, specific_items: [] });
              fetchPostsForType(pt);
            }}
            className="thha-select"
          >
            {availablePostTypes.map((pt) => (
              <option key={pt.value} value={pt.value}>
                {pt.label}
              </option>
            ))}
          </select>
        )}

        {/* Dynamic Taxonomy selector */}
        {node.type === 'taxonomy_archive' && (
          <select
            value={node.taxonomy_value || 'category'}
            onChange={(e) => handleUpdateCondition(node.id, { taxonomy_value: e.target.value })}
            className="thha-select"
          >
            {availableTaxonomies.map((tx) => (
              <option key={tx.value} value={tx.value}>
                {tx.label}
              </option>
            ))}
          </select>
        )}

        {/* Specific post dropdown */}
        {node.type === 'specific_post' && (
          <select
            value={(node.specific_items && node.specific_items[0]) || ''}
            onChange={(e) => {
              const val = e.target.value ? [parseInt(e.target.value, 10)] : [];
              handleUpdateCondition(node.id, { specific_items: val });
            }}
            className="thha-select"
          >
            <option value="">{__('Select Post...', 'huge-addons-for-elementor')}</option>
            {(cachedPosts['post'] || []).map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        )}

        {/* Specific page dropdown */}
        {node.type === 'specific_page' && (
          <select
            value={(node.specific_items && node.specific_items[0]) || ''}
            onChange={(e) => {
              const val = e.target.value ? [parseInt(e.target.value, 10)] : [];
              handleUpdateCondition(node.id, { specific_items: val });
            }}
            className="thha-select"
          >
            <option value="">{__('Select Page...', 'huge-addons-for-elementor')}</option>
            {(cachedPosts['page'] || []).map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        )}

        {/* Specific category dropdown */}
        {node.type === 'specific_category' && (
          <select
            value={(node.categories && node.categories[0]) || ''}
            onChange={(e) => {
              const val = e.target.value ? [parseInt(e.target.value, 10)] : [];
              handleUpdateCondition(node.id, { categories: val });
            }}
            className="thha-select"
          >
            <option value="">{__('Select Category...', 'huge-addons-for-elementor')}</option>
            {(cachedTerms['category'] || []).map((term) => (
              <option key={term.value} value={term.value}>
                {term.label}
              </option>
            ))}
          </select>
        )}

        {/* Remove button */}
        <button
          type="button"
          className="thha-btn-delete"
          onClick={() => handleRemoveCondition(node.id)}
          title={__('Remove Condition', 'huge-addons-for-elementor')}
        >
          ✕
        </button>
      </div>
    );
  };

  return (
    <div className="thha-conditions-container">
      {error && <div className="thha-alert thha-alert-danger">{error}</div>}

      {conflicts.length > 0 && (
        <div className="thha-alert thha-alert-warning">
          <strong>{__('Conflict Detected:', 'huge-addons-for-elementor')}</strong>{' '}
          {__(
            'This template was set to Draft because the same conditions are already active on:',
            'huge-addons-for-elementor'
          )}
          <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
            {conflicts.map((c) => (
              <li key={c.template_id}>
                <strong>{c.template_title}</strong> (ID: {c.template_id})
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="thha-conditions-list">
        {conditions.map((node) => renderConditionNode(node))}
      </div>

      <div className="thha-conditions-toolbar">
        <button type="button" className="thha-btn-secondary thha-btn-sm" onClick={handleAddCondition}>
          + {__('Add Condition', 'huge-addons-for-elementor')}
        </button>
        <button type="button" className="thha-btn-secondary thha-btn-sm" onClick={handleAddGroup}>
          + {__('Add Group', 'huge-addons-for-elementor')}
        </button>
      </div>

      <div className="thha-conditions-meta-row">
        <div className="thha-meta-field">
          <label htmlFor="thha-priority-input">
            {__('Priority (Lower number wins tie-breaks):', 'huge-addons-for-elementor')}
          </label>
          <input
            id="thha-priority-input"
            type="number"
            min="1"
            max="1000"
            value={priority}
            onChange={(e) => setPriority(parseInt(e.target.value, 10) || 10)}
            className="thha-input-number"
          />
        </div>

        {!isEmbedded && (
          <div className="thha-meta-field thha-status-field">
            <label className="thha-switch-label">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="thha-switch-input"
              />
              <span className="thha-switch-slider"></span>
              <span className="thha-switch-text">
                {isActive
                  ? __('Status: Published', 'huge-addons-for-elementor')
                  : __('Status: Draft', 'huge-addons-for-elementor')}
              </span>
            </label>
          </div>
        )}
      </div>

      {!isEmbedded && (
        <div className="thha-conditions-footer">
          <button
            type="button"
            className="thha-btn-primary"
            onClick={handleSave}
            disabled={loading}
          >
            {loading
              ? __('Saving...', 'huge-addons-for-elementor')
              : __('Save Display Conditions', 'huge-addons-for-elementor')}
          </button>
        </div>
      )}
    </div>
  );
};

export default HeaderFooterConditions;
